import { ArrayUtil } from "@coten-inc/utils";
import { parseISO } from "date-fns";
import { Copy, Globe2, Info } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { JsonTable } from "~/components/json-table";
import { Page } from "~/components/layout/Page";
import { SectionCard } from "~/components/section-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
} from "~/components/ui";
import { useGeoItemActivation, useGeoItemDetail } from "~/features/geoItems/hooks";
import { AreaViewMap } from "~/features/recordGeoItems";
import { geoItemDisplayName, geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import { parseCoords } from "~/features/recordGeoItems/utils/parseCoords";
import { parseGeometry } from "~/features/recordGeoItems/utils/parseGeometry";
import { v2Route } from "~/v2/routes";

interface GeoItemDetailProps {
  id: string;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,9rem)_1fr] items-start gap-4 py-2.5">
      <div className="text-[13px] font-medium capitalize text-fg-2">{label}</div>
      <div className="min-w-0 text-[13px] text-fg-1">{children}</div>
    </div>
  );
}

export function GeoItemDetail({ id }: GeoItemDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { geoItem, refetch } = useGeoItemDetail(id);
  const { onActivate, onDeactivate, processing } = useGeoItemActivation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCopyId = useCallback(async () => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  }, [id, t]);

  if (!geoItem) {
    return <Page className="text-[13px] text-fg-3">{t("ui.notFound")}</Page>;
  }

  const isDeactivated = geoItem.deactivatedAt != null;
  const label = geoItemDisplayName(geoItem.id, geoItemPlaceName(geoItem.i18nLabel, locale));
  const coords = geoItem.coords ? parseCoords(geoItem.coords) : null;
  const geometry = parseGeometry(geoItem.geometry);
  // One row per link entry, so a Record linking this GeoItem several times appears several times.
  const relatedRecords = ArrayUtil.uniqBy(
    geoItem.recordGeoItems.map(({ record }) => record),
    "id",
  );

  const handleConfirm = async () => {
    const ok = await (isDeactivated ? onActivate(id) : onDeactivate(id));
    setConfirmOpen(false);
    if (ok) void refetch();
  };

  return (
    <Page className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-dewey-title text-fg-1">{label}</h1>
            {isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-fg-3">
            <span className="flex items-center gap-1">
              {geoItem.id}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label={t("ui.copyId")}
                onClick={handleCopyId}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </span>
            <span>
              {t("model.common.createdAt")}: {t("date.short", { date: parseISO(geoItem.createdAt) })}
            </span>
            <span>
              {t("model.common.updatedAt")}: {t("date.short", { date: parseISO(geoItem.updatedAt) })}
            </span>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={processing} onClick={() => setConfirmOpen(true)}>
          {isDeactivated ? t("ui.activate") : t("ui.deactivate")}
        </Button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <SectionCard icon={Info} title={t("model.common.properties")}>
            <DetailRow label={t("model.geoItem.i18nLabel")}>
              <JsonTable target={geoItem.i18nLabel} />
            </DetailRow>
            <DetailRow label={t("model.geoItem.countryCodes")}>{geoItem.countryCode ?? "-"}</DetailRow>
            <DetailRow label={t("model.geoItem.relatedEntities")}>
              {relatedRecords.length === 0 ? (
                "-"
              ) : (
                <ul className="space-y-1">
                  {relatedRecords.map((record) => (
                    <li key={record.id}>
                      <Link
                        to={v2Route.entities.detail.$buildPath({ params: { id: record.id } })}
                        className="text-fg-link hover:text-primary-700"
                      >
                        {record.name || record.id}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </DetailRow>
          </SectionCard>

          <SectionCard icon={Info} title={t("model.common.metadata")}>
            <JsonTable target={geoItem.metadata} />
          </SectionCard>
        </div>

        <SectionCard icon={Globe2} title={t("ui.gis")}>
          {(geometry || geoItem.h3Indexes.length > 0) && (
            <div className="mb-4">
              <AreaViewMap areaH3Indexes={geoItem.h3Indexes} geometry={geometry} fitToGeometry />
            </div>
          )}
          <DetailRow label={t("model.geoItem.coords")}>{coords ? `${coords.lat}, ${coords.lng}` : "-"}</DetailRow>
          <DetailRow label={t("model.geoItem.h3Indexes")}>
            {geoItem.h3Indexes ? <JsonTable target={geoItem.h3Indexes} width="auto" /> : "-"}
          </DetailRow>
          <DetailRow label={t("model.geoItem.geometry")}>
            {geoItem.geometry ? <JsonTable target={geoItem.geometry} width="auto" /> : "-"}
          </DetailRow>
        </SectionCard>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeactivated
                ? t("ui.activateX", { x: t("model.geoItem.modelName") })
                : t("ui.deactivateX", { x: t("model.geoItem.modelName") })}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={processing} onClick={handleConfirm}>
              {isDeactivated ? t("ui.activate") : t("ui.deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
